import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { noop } from 'lodash-es';

import { IBaseProps } from '@rbi-ctg/frontend';
import useEffectOnce from 'hooks/use-effect-once';
import useIntl from 'state/intl/hook';
import { getCurrentVersion } from 'utils/appflow';
import { ILoggerOptions, defaultLogger } from 'utils/logger';

export type Logger = typeof defaultLogger;
export interface ILoggerContext {
  decorateLogger: <N extends object>(params: N, decorators?: ILoggerOptions) => void;
  logger: Logger;
}

export const LoggerContext = createContext<ILoggerContext>({
  decorateLogger: noop,
  logger: defaultLogger
} as ILoggerContext);

/**
 * use a logger, with optional params attached
 */
export const useLogger = <ChildFields extends object>(
  fields?: ChildFields,
  options?: ILoggerOptions
) => {
  const { logger } = useContext(LoggerContext);
  const loggerWithFields = useMemo(() => {
    if (!fields && !options) {
      return logger;
    }

    return logger.child(fields || {}, options);
  }, [logger, options, fields]);

  return loggerWithFields;
};

export const useLoggerContext = () => useContext(LoggerContext);

export const LoggerProvider = ({ children }: IBaseProps) => {
  const [logger, setLogger] = useState(defaultLogger);

  const { region } = useIntl();

  /**
   * decorateLogger allows additional fields to be attached to
   * the logger stored in context and exposed in `useLogger`
   */
  const decorateLogger = useCallback(
    <NewFields extends object>(params: NewFields, options: ILoggerOptions = {}) => {
      setLogger(currentLogger => currentLogger.child(params, options));
    },
    []
  );

  useEffectOnce(() => {
    getCurrentVersion().then(versionData => {
      if (versionData) {
        decorateLogger({
          appflowBuildId: versionData.buildId,
          appVersionCode: versionData.binaryVersionCode,
          appVersionName: versionData.binaryVersionName
        });
      }
    });
  });

  useEffect(() => {
    decorateLogger({ region });
  }, [decorateLogger, region]);

  return (
    <LoggerContext.Provider value={{ logger, decorateLogger }}>{children}</LoggerContext.Provider>
  );
};
